import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { WebinarOffer } from '@/store/webinar/types';
import { OfferTimer } from './OfferTimer';
import { ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OfferCardProps {
  variant: 'desktop' | 'mobile';
  data: Partial<WebinarOffer>;
  className?: string;
}

export function OfferCard({ variant, data, className }: OfferCardProps) {
  useLanguage();
  const formatPrice = (cents?: number): string => {
    if (!cents) return '';
    return (cents / 100).toLocaleString(getInterfaceLanguage(), {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const imageUrl = variant === 'desktop' ? data.img_desktop : data.img_mobile;
  const hasAnyBadge = data.badges?.limited_spots || data.badges?.today_only || 
                      data.badges?.bonus_time || data.badges?.custom;

  return (
    <div className={cn(
      'bg-background border rounded-2xl shadow-lg overflow-hidden',
      variant === 'mobile' ? 'max-w-sm' : 'max-w-2xl',
      className
    )}>
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between gap-4 mb-4">
          <h3 className="text-2xl font-bold text-foreground flex-1">
            {data.title || translateInterfaceText("Título da Oferta")}
          </h3>
          {hasAnyBadge && (
            <div className="flex flex-wrap gap-2 justify-end">
              {data.badges?.limited_spots && (
                <Badge variant="destructive">{translateInterfaceText("Vagas limitadas")}</Badge>
              )}
              {data.badges?.today_only && (
                <Badge variant="destructive">{translateInterfaceText("Somente hoje")}</Badge>
              )}
              {data.badges?.bonus_time && (
                <Badge variant="destructive">{translateInterfaceText("Bônus por tempo limitado")}</Badge>
              )}
              {data.badges?.custom && (
                <Badge variant="destructive">{data.badges.custom}</Badge>
              )}
            </div>
          )}
        </div>

        {data.subtitle && (
          <p className="text-muted-foreground">{data.subtitle}</p>
        )}
      </div>

      {/* Imagem ou Placeholder */}
      {imageUrl ? (
        <img 
          src={imageUrl} 
          alt={translateInterfaceText("Oferta")}
          className="w-full h-auto object-cover"
        />
      ) : (
        <div className="w-full h-48 bg-muted flex items-center justify-center">
          <ImageIcon className="h-16 w-16 text-muted-foreground" />
        </div>
      )}

      {/* Preços */}
      {(data.price_original || data.price_offer) && (
        <div className="px-6 py-4 space-y-2">
          {data.price_original && (
            <p className="text-lg text-muted-foreground line-through">
              {translateInterfaceText("De")}{" "}{formatPrice(data.price_original)}
            </p>
          )}
          {data.price_offer && (
            <p className="text-4xl font-bold text-foreground">
              {translateInterfaceText("Por")}{" "}{formatPrice(data.price_offer)}
            </p>
          )}
        </div>
      )}

      {/* Timer */}
      {data.timer_enabled && data.offer_end_sec && (
        <div className="px-6 pb-4">
          <OfferTimer 
            endSec={data.offer_end_sec} 
            label={data.timer_label || translateInterfaceText("Termina em:")}
          />
        </div>
      )}

      {/* CTAs */}
      <div className="p-6 pt-4 space-y-3">
        <Button
          className="w-full text-lg py-6"
          style={{ 
            backgroundColor: data.cta1_color || '#6366f1',
            color: '#ffffff'
          }}
        >
          {data.cta1_label || translateInterfaceText("Quero aproveitar")}
        </Button>

        {data.cta2_enabled && data.cta2_label && (
          <Button
            variant={data.cta2_style === 'ghost' ? 'ghost' : 'outline'}
            className="w-full"
          >
            {data.cta2_label}
          </Button>
        )}

        {data.reshow_at_end && (
          <Badge variant="secondary" className="w-full justify-center py-2">
            {translateInterfaceText("Reexibir no final do vídeo")}</Badge>
        )}
      </div>
    </div>
  );
}
